import * as React from 'react'
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { WebhookDeliveryDetailDto, WebhookDeliveryListDto, WebhookTestResultDto } from '@lib/dto/webhook-delivery.dto'
import type { EnvelopeWebhookEventName } from '@lib/enums/webhook-event.enum'
import type { WebhookDeliveryQuery } from '@lib/input/webhook-delivery.input'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { useRefreshOnForbidden } from '@/dashboard/useRefreshOnForbidden'
import { webhookLogKeys } from '@/hooks/queryKeys'
import { keepPreviousWithin } from '@/hooks/keepPreviousWithin'
import type { Uuid } from '@lib/validators/params'
import { WebhookLogService } from '@/services/WebhookLogService'

function useScopedService(): WebhookLogService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new WebhookLogService(client), [client])
}

export function useWebhookDeliveries(query: WebhookDeliveryQuery): UseQueryResult<WebhookDeliveryListDto> {
  const { tenantId, cacheKey, mode } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: webhookLogKeys.list(tenantId, cacheKey, mode, query),
    queryFn: () => service.list(tenantId, mode, query),
    placeholderData: keepPreviousWithin(webhookLogKeys.scope(tenantId, cacheKey, mode)),
  })
}

export function useWebhookDelivery(id: Uuid | null): UseQueryResult<WebhookDeliveryDetailDto> {
  const { tenantId, cacheKey, mode } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: webhookLogKeys.detail(tenantId, cacheKey, mode, id),
    queryFn: id !== null ? () => service.detail(tenantId, mode, id) : skipToken,
  })
}

/** Replay a delivery, or send a synthetic test event (test mode). Both refresh the log. */
export function useWebhookActions(): {
  replay: UseMutationResult<WebhookDeliveryDetailDto, Error, Uuid>
  sendTestEvent: UseMutationResult<WebhookTestResultDto, Error, EnvelopeWebhookEventName>
} {
  const { tenantId, cacheKey, mode } = useDashboardScope()
  const service = useScopedService()
  const queryClient = useQueryClient()
  const onError = useRefreshOnForbidden()
  const onSettled = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: webhookLogKeys.scope(tenantId, cacheKey, mode) })
  return {
    replay: useMutation({ mutationFn: (id) => service.replay(tenantId, mode, id), onError, onSettled }),
    sendTestEvent: useMutation({ mutationFn: (event) => service.sendTestEvent(tenantId, event), onError }),
  }
}
